import React, { useContext, useEffect, useState } from 'react';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Box from '@amzn/meridian/box';
import { AssociateCardSummary, ReportingTeamUser } from '../../../Types';
import UserBadge from '../../Dashboard/DashboardComponents/UserBadge';
import Divider from '@amzn/meridian/divider';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import closeLargeTokens from '@amzn/meridian-tokens/base/icon/close-large';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import styles from './AssociateRichCardsStyle.module.css';
import { coachingStatusLabels, getTrendIndicator } from './Util/helper';
import { renderUrl, urls } from '../../../Util';
import chevronRightLargeTokens from '@amzn/meridian-tokens/base/icon/chevron-right-large';
import { GoalPeriodType, MetricsOption } from '@amzn/aet-galactus-kit';
import { getMetricStringIdAndDefaultMessage } from '../Table/LighthouseMetrics/helper';
import ExpandedContactCard from './ExpandedContactCard';
import TrendTextIndicator from './Components/TrendTextIndicator';
import useCustomTracking from '../../../Util/useCustomTracking';
import { StateContext } from '../../../GlobalStateStore';
import { formatGoalToString, getRecentMonths } from '../../Goals/helper';
import { ARCFilters, RELATION_TO_USER, SIDE_MENU_PATH } from './Types/types';
import moment from 'moment';
import { Feature, isFeatureEnabled } from '../../../config/FeaturesConfig';
import {
  computeMetricDifference,
  filterCCXMetricsForRemoval,
  formatMetric,
  getBottomQuartileMetrics,
  getLargestBottomPercentile,
} from '../Table/TimeTrends/helper';
import { TIME_TABLE } from '../../Dashboard/DashboardComponents/LighthouseMetrics/helper';
import Tag from '@amzn/meridian/tag';
import BottomQuartileTag from '../Table/TimeTrends/BottomQuartileTag';
import { BottomQuartileCategory } from '../Table/TimeTrends/types';
import DataSource from '../../../api';
import { ConsolidatedContact } from '../../../api/CoachingNotes/schema';
import DataState from '../../../Types/DataState';
import { processContact } from './Util/ARCRecommendationFilter';
import Loader from '@amzn/meridian/loader';
import { NO_DATA_LABEL } from './Util/team';
import { formatWeeklyDateRange } from '../../../Util/DateHelper';
import { useNavigate } from 'react-router';
import Naws from '../../../api/Naws';
import { ManagerWeeklyHighlightsDisclaimer } from '../../CSASummaries/common/Alerts';
import CSASummariesContent, {
  CSASummariesContentProps,
} from '../../CSASummaries/CSASummariesContent';
import { ViewContext } from '../../CSASummaries/types';
import useIsCSASummariesEnabled from '../../CSASummaries/hooks/useIsCSASummariesEnabled';
import { useProfiles } from '../../hooks/useProfiles';
import useHasHadSummaries from '../../CSASummaries/hooks/useHasHadSummaries';
import CenteredLoader from '../../../commons/CenteredLoader';

export interface AssociateCardExpandedProps {
  focusProfile: ReportingTeamUser;
  setExpanded: any;
  setFocusProfile: any;
  associateCardSummary: AssociateCardSummary[];
  hash: string;
  currentVertical: string;
  setHash: React.Dispatch<any>;
}

const AssociateCardExpanded: React.FC<AssociateCardExpandedProps> = ({
  focusProfile,
  setExpanded,
  setFocusProfile,
  associateCardSummary,
  hash,
  currentVertical,
  setHash,
}: AssociateCardExpandedProps): React.ReactElement => {
  const apiNaws: typeof Naws = DataSource.getDataFeed('NAWS');
  const [focusedBottomQuartile, setFocusedBottomQuartile] =
    useState<{ metric: string; value: string; netAvg: string }[]>();
  const [isBottomQuartile, setIsBottomQuartile] = useState<boolean>(false);
  const [routingInfo, setRoutingInfo] = useState<string>('');
  const enableHighSilence = isFeatureEnabled(Feature.HIGH_SILENCE_RATE);
  const { loggedInProfile } = useProfiles();

  const navigate = useNavigate();

  const intl = useIntl();
  const [focusAssociateCardSummary, setFocusAssociateCardSummary] =
    useState<AssociateCardSummary>();
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, requesterUserID, requesterUsername, userID, cscEntityPrefix } =
    inspireOptions.data;

  const sharedProfileUser =
    focusProfile.relatedUser.relationToUser === RELATION_TO_USER.COACHEE;

  const [past4WeekcontactList, setPast4WeekContactList] = useState<
    ConsolidatedContact[]
  >([]);
  const [past4WeekContactListState, setPast4WeekContactListState] = useState(
    DataState.LOADING
  );
  const concessionFeatureEnabled = isFeatureEnabled(
    Feature.ENABLE_CONCESSION_COMPLIANCE
  );
  const SACoaching = isFeatureEnabled(Feature.SA_COACHING);

  const csaSummariesFeatureEnabled = useIsCSASummariesEnabled({
    managerId: loggedInProfile?.managers[0]?.userID,
  });
  // we may eventually want to show the TM's CSA summaries view if an OM is viewing a TM here
  const shouldShowCSASummaries =
    csaSummariesFeatureEnabled && !sharedProfileUser && !focusProfile.isManager;

  const [processedContact, setProcessedContact] = useState<
    ConsolidatedContact[]
  >([]);

  const [processedContactState, setProcessedContactState] = useState(
    DataState.LOADING
  );

  useEffect(() => {
    if (past4WeekContactListState === DataState.LOADED) {
      const { contacts: preprocessedContact } = processContact(
        past4WeekcontactList.filter(c => c.recommended !== 0),
        concessionFeatureEnabled,
        SACoaching
      );
      setProcessedContact(preprocessedContact);
      setProcessedContactState(DataState.LOADED);
    } else {
      setProcessedContactState(DataState.LOADING);
    }
  }, [past4WeekContactListState, past4WeekcontactList]);

  const compareByButtonGroupClickStreamContentId = `metrics-compare-data-by-some-button`;
  const [
    DateSelectionButtonGroupClickStreamTrackingTag,
    emitDateSelectionClickstreamTags,
  ] = useCustomTracking(
    compareByButtonGroupClickStreamContentId,
    'metrics-slot',
    {
      'data-csa-c-type': 'button',
      'data-csa-c-content-id': compareByButtonGroupClickStreamContentId,
      'data-csa-c-tag1': 'Metrics',
      'data-csa-c-profile-id': requesterUserID,
      'data-csa-c-username': requesterUsername,
      'data-csa-c-is-tm': isTm,
    }
  );

  const formattedDateRange = formatWeeklyDateRange(intl);
  const lastMonth = getRecentMonths(-1)[0];
  const formattedMonths = `${lastMonth.month} ${lastMonth.year}`;

  useEffect(() => {
    if (focusProfile !== null) {
      const focusSummary = associateCardSummary.find(
        summary => summary.userAlias === focusProfile.relatedUser.alias
      );
      const isBottom =
        (focusSummary && getBottomQuartileMetrics(focusSummary).length > 0) ??
        false;

      setIsBottomQuartile(isBottom);
      if (!isBottom) return;

      const metricFromLastMonth = focusSummary?.metricsFromPreviousMonth;
      // Filter out CCX metrics from bottom quartile display when enableCCXRemoval is enabled
      const filteredMetrics = filterCCXMetricsForRemoval(
        metricFromLastMonth ?? [],
        m => m.metric
      );
      setFocusedBottomQuartile(filteredMetrics);
    } else {
      setIsBottomQuartile(false);
      setFocusedBottomQuartile([]);
    }
  }, [focusProfile]);

  useEffect(() => {
    if (!associateCardSummary || associateCardSummary.length === 0) return;

    const foundAssociateCardSummary = associateCardSummary.find(
      associateCard => associateCard.userID === focusProfile.relatedUser.userID
    );

    if (foundAssociateCardSummary) {
      setFocusAssociateCardSummary(foundAssociateCardSummary);
      setRoutingInfo(
        `bottomUser=${
          focusProfile.relatedUser.alias
        }&bottomMetrics=${getBottomQuartileMetrics(
          foundAssociateCardSummary
        )}&bottomPercentile=${getLargestBottomPercentile(
          foundAssociateCardSummary
        )}`
      );
    }
  }, [associateCardSummary, focusProfile]);

  useEffect(() => {
    if (focusProfile.relatedUser) {
      // GGGG refers to the year of the week. This may be different from YYYY
      // When part of the week is from 2024 and part of it is from 2025. e.g. 2024-12-30 (Monday)
      // In such cases, GGGG refers to the new year (e..g 2025) and YYYY refers to the previous year (e.g. 2024).
      const startWeek = moment().subtract(4, 'w').format('GGGG-WW');
      const endWeek = moment().format('GGGG-WW');

      const userID = `${cscEntityPrefix}:${focusProfile.relatedUser.userID}`;

      const queryVariablesNaws = apiNaws.prepareQueryforRecommendationsGetter(
        userID,
        focusProfile.relatedUser.userName,
        startWeek,
        endWeek
      );
      if (!focusProfile.isManager) {
        apiNaws.getConsolidatedContacts(
          queryVariablesNaws,
          setPast4WeekContactList,
          setPast4WeekContactListState
        );
      }
    }
  }, [focusProfile]);

  const [sideMenuSource] = hash.includes(SIDE_MENU_PATH.DIRECT_REPORT)
    ? [SIDE_MENU_PATH.DIRECT_REPORT]
    : hash.includes(SIDE_MENU_PATH.SHARED_PROFILE)
      ? [SIDE_MENU_PATH.SHARED_PROFILE]
      : [SIDE_MENU_PATH.ALL_ASSOCIATES];
  const enableGoalSetting = isFeatureEnabled(Feature.GOAL_SETTING);

  return (
    <>
      {focusAssociateCardSummary && (
        <Box>
          <Row
            alignmentHorizontal='justify'
            width='100%'
            alignmentVertical='top'
          >
            <Row alignmentHorizontal='start' alignmentVertical='center'>
              <UserBadge username={focusProfile.relatedUser.alias} thumbNail />
              <Column spacing='300'>
                <Row>
                  <Text type='h400'>
                    {focusProfile.relatedUser.displayName}
                  </Text>
                </Row>
                {isBottomQuartile && !focusProfile.isManager && (
                  <>
                    <Row data-cy='tag-expanded-bottom-quartile-row'>
                      <Tag type='warning'>
                        {intl.formatMessage({
                          id: 'cs_perfportal_bq',
                          defaultMessage: 'Bottom Quartile',
                        })}
                      </Tag>
                    </Row>
                    <Row>
                      <Text>
                        <FormattedMessage
                          id='cs_perfportal_quartile_status_description'
                          defaultMessage="Quartile status is based on the previous month's data."
                        />
                      </Text>
                    </Row>
                  </>
                )}
              </Column>
            </Row>

            <Row alignmentHorizontal='end' alignmentVertical='top'>
              <Button
                type='link'
                data-csa-c-type='button'
                data-csa-c-slot-id='arc-expanded-collapse-button'
                data-csa-c-tag1='Team'
                data-csa-c-tag2='Associates'
                data-csa-c-tag3='ExpandedView'
                data-csa-c-profile-id={requesterUserID}
                data-csa-c-username={requesterUsername}
                data-csa-c-is-tm={isTm}
                data-csa-c-content-id={`arc-expanded-collapse-button-${focusProfile.relatedUser.userID}`}
                onClick={() => {
                  setExpanded(false);
                  setFocusProfile(null);
                  setHash(sideMenuSource); // Set back to parment menu
                  setProcessedContactState(DataState.LOADING);
                }}
              >
                <Icon tokens={closeLargeTokens} />
              </Button>
            </Row>
          </Row>
          <Row spacingInset='200 none none'>
            <Text type='b300'>
              <span className={styles.subdued}>
                @{focusProfile.relatedUser.alias}
              </span>
            </Text>
          </Row>
          {!focusProfile.isManager && (
            <>
              <Row width='100%' spacingInset='400 none'>
                <Divider />
              </Row>
              <Row width='100%' widths={['50%', '50%']}>
                <Box>
                  <Heading level={3} type='h300'>
                    <FormattedMessage
                      id='cs_perfportal_arc_recent_coachings'
                      defaultMessage='Recent Coachings'
                    />
                  </Heading>
                </Box>
                <Row alignmentHorizontal='end'>
                  <Button
                    size='small'
                    href={renderUrl(
                      `${urls.coachingNotes}/${focusProfile.relatedUser.userID}`
                    )}
                    onClick={navigate}
                    data-csa-c-type='button'
                    data-csa-c-slot-id='arc-expanded-open-coaching-notes-button-slot'
                    data-csa-c-content-id={`arc-expanded-open-coaching-notes-for-${focusProfile.relatedUser.userID}`}
                    data-csa-c-tag1='Team'
                    data-csa-c-tag2='Associates'
                    data-csa-c-tag3='ExpandedView'
                    data-csa-c-tag4='RecentCoachings'
                    data-csa-c-profile-id={requesterUserID}
                    data-csa-c-username={requesterUsername}
                    data-csa-c-is-tm={isTm}
                    type='tertiary'
                  >
                    <FormattedMessage
                      id='cs_perfportal_arc_go_to_coaching_notes'
                      defaultMessage='Go to Coaching Notes'
                    />
                    <Icon tokens={chevronRightLargeTokens} />
                  </Button>
                </Row>
              </Row>
              {focusAssociateCardSummary.coachings?.lastCoachingStatus !==
              undefined ? (
                <Row
                  width='100%'
                  widths={['50%', '50%']}
                  spacingInset='400 none'
                >
                  <Row width='100%'>
                    <Text type='h100'>
                      <FormattedMessage
                        id='cs_perfportal_arc_coaching_status'
                        defaultMessage='Coaching Status'
                      />
                    </Text>
                    <Text type='b300'>
                      {focusAssociateCardSummary.coachings
                        ?.lastCoachingStatus !== undefined
                        ? coachingStatusLabels(
                            focusAssociateCardSummary.coachings
                              .lastCoachingStatus
                          )
                        : '--'}
                    </Text>
                  </Row>

                  <Row width='100%'>
                    <Text type='h100'>
                      <FormattedMessage
                        id='cs_perfportal_arc_last_coach_date'
                        defaultMessage='Last Coach Date'
                      />
                    </Text>
                    <Text type='b300'>
                      {moment
                        .utc(
                          focusAssociateCardSummary.coachings?.lastCoachedDate
                        )
                        .isValid()
                        ? moment
                            .utc(
                              focusAssociateCardSummary.coachings
                                ?.lastCoachedDate
                            )
                            .format('MMM. D, YYYY')
                            .toLocaleString()
                        : '--'}
                    </Text>
                  </Row>
                </Row>
              ) : (
                <FormattedMessage
                  id='cs_perfportal_arc_no_recent_coachings'
                  defaultMessage='No recent coachings'
                />
              )}
            </>
          )}
          <Row width='100%' spacingInset='400 none'>
            <Divider />
          </Row>
          {shouldShowCSASummaries && (
            <CSASummariesSection
              key={focusProfile.relatedUser.alias}
              CSASummariesContentProps={{
                displayedCsa: focusProfile.relatedUser,
                viewContext:
                  focusProfile.relatedUser.relationToUser ===
                  RELATION_TO_USER.DIRECT_REPORT
                    ? ViewContext.DIRECT_REPORT_FROM_TEAM // only show the feedback buttons if viewing a direct report
                    : ViewContext.OTHER,
              }}
            />
          )}
          <Row width='100%' widths={['50%', '50%']}>
            <Box spacingInset='none'>
              <Heading level={3} type='h300'>
                <FormattedMessage
                  id='cs_perfportal_performance'
                  defaultMessage='Performance'
                />
              </Heading>
            </Box>
          </Row>
          <Row
            width='100%'
            widths={['50%', '50%']}
            spacingInset='400 none none'
          >
            <Box spacingInset='none'>
              <Heading level={3} type='h200'>
                <FormattedMessage
                  id='cs_perfportal_performance_trends'
                  defaultMessage='Performance Trends | {range}'
                  values={{
                    range: formattedDateRange,
                  }}
                />
              </Heading>
            </Box>
            <Row alignmentHorizontal='end'>
              {!sharedProfileUser && (
                <Button
                  size='small'
                  href={renderUrl(
                    `${urls.metrics}/${userID}?selectedCSA=${focusProfile.relatedUser.userID}&selectedVertical=${currentVertical}`
                  )}
                  onClick={navigate}
                  data-csa-c-type='button'
                  data-csa-c-slot-id='arc-expanded-open-metrics-button-slot'
                  data-csa-c-content-id={`arc-expanded-open-metrics-for-${focusProfile.relatedUser.userID}`}
                  data-csa-c-tag1='Team'
                  data-csa-c-tag2='Associates'
                  data-csa-c-tag3='ExpandedView'
                  data-csa-c-tag4='Performance'
                  data-csa-c-profile-id={requesterUserID}
                  data-csa-c-username={requesterUsername}
                  data-csa-c-is-tm={isTm}
                  type='tertiary'
                >
                  <FormattedMessage
                    id='cs_perfportal_arc_view_all_metrics_button'
                    defaultMessage='View All Metrics'
                  />
                  <Icon tokens={chevronRightLargeTokens} />
                </Button>
              )}
            </Row>
          </Row>
          <Row
            width='100%'
            widths={['50%', '50%']}
            spacingInset='300 none none'
          >
            <Box spacingInset='none'>
              <Text type='b300'>
                {' '}
                <span className={styles.arcDescription}>
                  <FormattedMessage
                    id='cs_perfportal_weekly_trends_compare'
                    defaultMessage='Weekly trends compare changes in metric data over the past two weeks.'
                  />
                </span>
              </Text>
            </Box>
          </Row>
          <Row
            width='100%'
            widths={['237px', '237px', '494px']}
            alignmentVertical='top'
            spacingInset='400 none none'
          >
            <Box width='297'>
              <Text type='h100'>
                <FormattedMessage
                  id='cs_perfportal_metrics_metric_type'
                  defaultMessage='Metric Type'
                />
              </Text>
            </Box>
            <Box width='177'>
              <Text type='h100' alignment='right'>
                <FormattedMessage
                  id='cs_perfportal_sort_metric_by_metric_value'
                  defaultMessage='Metric Value'
                />
              </Text>
            </Box>
            <Box width='494'>
              <Text type='h100'>
                <FormattedMessage
                  id='cs_perfportal_weekly_trend'
                  defaultMessage='Weekly Trend'
                />
              </Text>
            </Box>
          </Row>
          {focusAssociateCardSummary.performance &&
          focusAssociateCardSummary.performance.trendSummary.length > 0 ? (
            focusAssociateCardSummary.performance.trendSummary.map(trend => {
              if (
                !enableHighSilence &&
                trend?.metric === MetricsOption.HIGH_SILENCE_RATE
              )
                return <></>;
              return (
                trend && (
                  <Row
                    width='100%'
                    widths={['297px', '177px', '494px']}
                    spacingInset='300 none none'
                    key={trend.metric + trend.trendIndicator}
                  >
                    {trend && (
                      <>
                        <Box>
                          <Text type='b300'>
                            {intl.formatMessage({
                              ...getMetricStringIdAndDefaultMessage(
                                trend.metric
                              ),
                            })}
                          </Text>
                        </Box>
                        <Box>
                          <Text type='b300' alignment='right'>
                            {formatMetric(trend?.endValue)}
                          </Text>{' '}
                        </Box>
                        <Box>
                          <TrendTextIndicator
                            metric={trend.metric}
                            label={trend?.delta?.toString() ?? NO_DATA_LABEL}
                            trend={trend?.trendIndicator ?? 'neutral'}
                            showLongTrendDescription={true}
                          />
                        </Box>
                      </>
                    )}
                  </Row>
                )
              );
            })
          ) : (
            <FormattedMessage
              id='cs_perfportal_arc_no_summary'
              defaultMessage='No summary available'
            />
          )}

          {isBottomQuartile && !focusProfile.isManager && (
            <>
              <Row
                width='100%'
                widths={['50%', '50%']}
                spacingInset='400 none none'
              >
                <Box>
                  <Text type='h200'>
                    <FormattedMessage
                      id='cs_perfportal_bottom_quartile_trends'
                      defaultMessage='Bottom Quartile | {range}'
                      values={{
                        range: formattedMonths,
                      }}
                    />
                  </Text>
                </Box>
                {!sharedProfileUser &&
                  (focusAssociateCardSummary?.bottomQuartileMetrics
                    ?.metricSummary?.length ?? 0) > 0 && (
                    <Row alignmentHorizontal='end'>
                      <Button
                        type='tertiary'
                        href={renderUrl(
                          routingInfo && routingInfo != ''
                            ? `${urls.metrics}/${userID}?${routingInfo}&selectedVertical=${currentVertical}#nav-bottom-quartile`
                            : `${urls.metrics}/${userID}?selectedVertical=${currentVertical}#nav-bottom-quartile`
                        )}
                        onClick={navigate}
                        size='small'
                        data-csa-c-type='button'
                        data-csa-c-slot-id='arc-expanded-view-bottom-quartile-performance-button-slot'
                        data-csa-c-content-id={`arc-expanded-view-bottom-quartile-performance-for-${focusProfile.relatedUser.userID}`}
                        data-csa-c-tag1='Team'
                        data-csa-c-tag2='Associates'
                        data-csa-c-tag3='ExpandedView'
                        data-csa-c-tag4='Performance'
                        data-csa-c-profile-id={requesterUserID}
                        data-csa-c-username={requesterUsername}
                        data-csa-c-is-tm={isTm}
                      >
                        <FormattedMessage
                          id='cs_perfportal_bq_view_bottom_quartile_button'
                          defaultMessage='View Bottom Quartile Performance'
                        />
                        <Icon tokens={chevronRightLargeTokens} />
                      </Button>
                    </Row>
                  )}
              </Row>
              <Row width='100%' widths='100%' spacingInset='300 none none'>
                <Box spacingInset='none'>
                  <Text type='b300'>
                    {' '}
                    <span className={styles.arcDescription}>
                      <FormattedMessage
                        id='cs_perfportal_quartile_ranking_based'
                        defaultMessage="Quartile ranking is based on the last month's performance compared to the network average."
                      />
                    </span>
                  </Text>
                </Box>
              </Row>
              <Row
                width='100%'
                widths={['297px', '177px', '241px', '253px']}
                alignmentVertical='top'
                spacingInset='400 none none'
              >
                <Box>
                  <Text type='h100'>
                    <FormattedMessage
                      id='cs_perfportal_metrics_metric_type'
                      defaultMessage='Metric Type'
                    />
                  </Text>
                </Box>
                <Box>
                  <Text type='h100' alignment='right'>
                    <FormattedMessage
                      id='cs_perfportal_sort_metric_by_metric_value'
                      defaultMessage='Metric Value'
                    />
                  </Text>
                </Box>
                <Box>
                  <Text type='h100'>
                    <FormattedMessage
                      id='cs_perfportal_bottom_quartile_ranking'
                      defaultMessage='Bottom Quartile Status'
                    />
                  </Text>
                </Box>
                <Box>
                  <Text type='h100'>
                    <FormattedMessage
                      id='cs_perfportal_network_trends'
                      defaultMessage='Network Trends'
                    />
                  </Text>
                </Box>
              </Row>
              {focusedBottomQuartile!.map(metricData => {
                const delta = computeMetricDifference(
                  metricData.value,
                  metricData.netAvg,
                  true
                );
                const isTimeMetric =
                  metricData.metric in TIME_TABLE
                    ? TIME_TABLE[metricData.metric as keyof typeof TIME_TABLE]
                    : false;

                const label =
                  associateCardSummary
                    ?.find(
                      summary =>
                        summary?.userAlias === focusProfile?.relatedUser?.alias
                    )
                    ?.bottomQuartileMetrics?.metricSummary?.find(
                      item => item?.metric === metricData.metric
                    )?.label ?? null;
                return (
                  <Box>
                    <Row
                      width='100%'
                      widths={['297px', '177px', '241px', '253px']}
                      alignmentVertical='top'
                      spacingInset='300 none none'
                    >
                      <>
                        <Text type='b300'>
                          {intl.formatMessage({
                            ...getMetricStringIdAndDefaultMessage(
                              metricData.metric
                            ),
                          })}
                        </Text>
                        <Text type='b300' alignment='right'>
                          {formatMetric(
                            isTimeMetric
                              ? metricData.value
                              : (Number(metricData.value) * 100).toString()
                          )}
                        </Text>
                        {label && (
                          <Box>
                            <BottomQuartileTag
                              bqCategory={label as BottomQuartileCategory}
                            />
                          </Box>
                        )}
                        <Box>
                          <TrendTextIndicator
                            metric={metricData.metric}
                            label={delta ?? NO_DATA_LABEL}
                            trend={getTrendIndicator(delta, isTimeMetric)}
                            showLongTrendDescription={false}
                            showNetworkDescription={true}
                          />
                        </Box>
                      </>
                    </Row>
                  </Box>
                );
              })}
            </>
          )}
          <Row width='100%' spacingInset='400 none'>
            <Divider />
          </Row>

          {enableGoalSetting && !focusProfile.isManager && (
            <Column>
              <Row width='100%' widths={['50%', '50%']}>
                <Box>
                  <Heading level={3} type='h300'>
                    <FormattedMessage
                      id='cs_perfportal_arc_active_goals_heading'
                      defaultMessage='Active Goals'
                    />
                  </Heading>
                </Box>
                {!sharedProfileUser && (
                  <Row alignmentHorizontal='end'>
                    <Button
                      size='small'
                      href={renderUrl(
                        `${urls.coachingNotes}/${focusProfile.relatedUser.userID}#nav-goals`
                      )}
                      onClick={navigate}
                      data-csa-c-type='button'
                      data-csa-c-slot-id='arc-expanded-open-goals-button-slot'
                      data-csa-c-content-id={`arc-expanded-open-goals-for-${focusProfile.relatedUser.userID}`}
                      data-csa-c-tag1='Team'
                      data-csa-c-tag2='Associates'
                      data-csa-c-tag3='ExpandedView'
                      data-csa-c-tag4='OpenGoals'
                      data-csa-c-profile-id={requesterUserID}
                      data-csa-c-username={requesterUsername}
                      data-csa-c-is-tm={isTm}
                      type='tertiary'
                    >
                      <FormattedMessage
                        id='cs_perfportal_arc_go_to_goals_button'
                        defaultMessage='View All Goals'
                      />
                      <Icon tokens={chevronRightLargeTokens} />
                    </Button>
                  </Row>
                )}
              </Row>
              <Row
                width='100%'
                widths={['50%', '50%']}
                alignmentVertical='top'
                data-testid='weekly-goals'
              >
                <Column alignmentVertical='top'>
                  <Text type='h100'>
                    <FormattedMessage
                      id='cs_perfportal_arc_weekly_goals'
                      defaultMessage='Weekly Goals'
                    />
                  </Text>
                  {focusAssociateCardSummary.goals &&
                  focusAssociateCardSummary.goals.length > 0 ? (
                    focusAssociateCardSummary.goals
                      .filter(goal => goal.periodType === GoalPeriodType.Week)
                      .slice(0, 6)
                      .map(
                        (goal, index) =>
                          goal && (
                            <Text type='b300'>
                              {formatGoalToString(goal, intl)}
                            </Text>
                          )
                      )
                  ) : (
                    <Text type='b300'>
                      <FormattedMessage
                        id='cs_perfportal_arc_no_open_goals'
                        defaultMessage='No active goals'
                      />
                    </Text>
                  )}
                </Column>
                <Column alignmentVertical='top' data-testid='monthly-goals'>
                  <Text type='h100'>
                    <FormattedMessage
                      id='cs_perfportal_arc_monthly_goals'
                      defaultMessage='Monthly Goals'
                    />
                  </Text>
                  {focusAssociateCardSummary.goals &&
                  focusAssociateCardSummary.goals.length > 0 ? (
                    focusAssociateCardSummary.goals
                      .filter(goal => goal.periodType === GoalPeriodType.Month)
                      .slice(0, 6)
                      .map((goal, index) => (
                        <Text type='b300'>
                          {formatGoalToString(goal, intl)}
                        </Text>
                      ))
                  ) : (
                    <Text type='b300'>
                      <FormattedMessage
                        id='cs_perfportal_arc_no_open_goals'
                        defaultMessage='No active goals'
                      />
                    </Text>
                  )}
                </Column>
              </Row>
              <Row width='100%' spacingInset='400 none'>
                <Divider />
              </Row>
            </Column>
          )}
          {!focusProfile.isManager && (
            <>
              <Row width='100%' widths={['50%', '50%']}>
                <Box>
                  <Heading level={3} type='h300'>
                    <FormattedMessage
                      id='cs_perfportal_opportunities'
                      defaultMessage='Opportunities'
                    />
                  </Heading>
                </Box>
                <Row alignmentHorizontal='end'>
                  <Button
                    size='small'
                    href={renderUrl(
                      `${urls.coachingNotes}/${focusProfile.relatedUser.userID}#nav-recommended`
                    )}
                    onClick={navigate}
                    data-csa-c-type='button'
                    data-csa-c-slot-id='arc-expanded-open-recommended-categories-button-slot'
                    data-csa-c-content-id={`arc-expanded-open-recommended-categories-for-${focusProfile.relatedUser.userID}`}
                    data-csa-c-tag1='Team'
                    data-csa-c-tag2='Associates'
                    data-csa-c-tag3='ExpandedView'
                    data-csa-c-tag4='Opportunities'
                    data-csa-c-profile-id={requesterUserID}
                    data-csa-c-username={requesterUsername}
                    data-csa-c-is-tm={isTm}
                    type='tertiary'
                  >
                    <FormattedMessage
                      id='cs_perfportal_arc_go_to_recommendation_button'
                      defaultMessage='View All Recommendations'
                    />
                    <Icon tokens={chevronRightLargeTokens} />
                  </Button>
                </Row>
              </Row>
              {focusAssociateCardSummary.opportunities &&
              focusAssociateCardSummary.opportunities.labelAndCount &&
              focusAssociateCardSummary.opportunities.labelAndCount.length >
                0 &&
              focusAssociateCardSummary.opportunities.labelAndCount[0]
                .category ? (
                processedContactState === DataState.LOADED ? (
                  <>
                    <Row spacingInset='400 none none'>
                      <Text type='b300'>
                        <FormattedMessage
                          id='cs_perfportal_arc_recommneded_behavior_top_heading'
                          defaultMessage="{firstName}'s top recommended coachable behaviors from the past 7 days."
                          values={{
                            firstName: focusProfile.relatedUser.firstName,
                          }}
                        />
                      </Text>
                    </Row>
                    <Row
                      width='100%'
                      widths={['33.3%', '33.3%', '33.3%']}
                      spacingInset='400 none'
                      alignmentVertical='stretch'
                    >
                      {focusAssociateCardSummary.opportunities &&
                        focusAssociateCardSummary.opportunities.labelAndCount
                          .slice(0, 3)
                          .map((opportunity, index) => (
                            <ExpandedContactCard
                              associateCardSummary={focusAssociateCardSummary}
                              displayIndex={index}
                              category={ARCFilters.OPPORTUNITIES}
                              recomContacts={processedContact}
                            />
                          ))}
                    </Row>
                  </>
                ) : (
                  <Row alignmentHorizontal='center'>
                    <Loader type='circular' size='large' />
                  </Row>
                )
              ) : (
                <FormattedMessage
                  id='cs_perfportal_arc_no_opportunities'
                  defaultMessage='No current opportunities.'
                />
              )}
              <Row width='100%' spacingInset='400 none'>
                <Divider />
              </Row>
              <Row width='100%' widths={['50%', '50%']}>
                <Box>
                  <Heading level={3} type='h300'>
                    <FormattedMessage
                      id='cs_perfportal_kuods'
                      defaultMessage='Kudos'
                    />
                  </Heading>
                </Box>
                <Row alignmentHorizontal='end'>
                  <Button
                    size='small'
                    href={renderUrl(
                      `${urls.coachingNotes}/${focusProfile.relatedUser.userID}#nav-recommended`
                    )}
                    onClick={navigate}
                    data-csa-c-type='button'
                    data-csa-c-slot-id='arc-expanded-open-recommended-categories-button-slot'
                    data-csa-c-content-id={`arc-expanded-open-recommended-categories-for-${focusProfile.relatedUser.userID}`}
                    data-csa-c-tag1='Team'
                    data-csa-c-tag2='Associates'
                    data-csa-c-tag3='ExpandedView'
                    data-csa-c-tag4='Kudos'
                    data-csa-c-profile-id={requesterUserID}
                    data-csa-c-username={requesterUsername}
                    data-csa-c-is-tm={isTm}
                    type='tertiary'
                  >
                    <FormattedMessage
                      id='cs_perfportal_arc_go_to_recommendation_button'
                      defaultMessage='View All Recommendations'
                    />
                    <Icon tokens={chevronRightLargeTokens} />
                  </Button>
                </Row>
              </Row>
              {focusAssociateCardSummary.kudos &&
              focusAssociateCardSummary.kudos.labelAndCount &&
              focusAssociateCardSummary.kudos.labelAndCount.length > 0 &&
              focusAssociateCardSummary.kudos.labelAndCount[0].category ? (
                processedContactState === DataState.LOADED ? (
                  <>
                    <Row spacingInset='400 none none'>
                      <Text type='b300'>
                        <FormattedMessage
                          id='cs_perfportal_arc_recommneded_kudos_top_heading'
                          defaultMessage="{firstName}'s top kudos from the past 7 days."
                          values={{
                            firstName: focusProfile.relatedUser.firstName,
                          }}
                        />
                      </Text>
                    </Row>

                    <Row
                      width='100%'
                      widths={['33.3%', '33.3%', '33.3%']}
                      spacingInset='400 none'
                      alignmentVertical='stretch'
                    >
                      {focusAssociateCardSummary.kudos &&
                        focusAssociateCardSummary.kudos.labelAndCount
                          .slice(0, 3)
                          .map((kudos, index) => (
                            <ExpandedContactCard
                              associateCardSummary={focusAssociateCardSummary}
                              displayIndex={index}
                              category={ARCFilters.KUDOS}
                              recomContacts={processedContact}
                            />
                          ))}
                    </Row>
                  </>
                ) : (
                  <Row alignmentHorizontal='center'>
                    <Loader type='circular' size='large' />
                  </Row>
                )
              ) : (
                <FormattedMessage
                  id='cs_perfportal_arc_no_kudos'
                  defaultMessage='No kudos at this time.'
                />
              )}
            </>
          )}
        </Box>
      )}
    </>
  );
};

interface CSASummariesSectionProps {
  CSASummariesContentProps: CSASummariesContentProps;
}

const CSASummariesSection = ({
  CSASummariesContentProps,
}: CSASummariesSectionProps) => {
  const { loading, hasHadSummary } = useHasHadSummaries({
    alias: CSASummariesContentProps.displayedCsa.alias,
    isProfileTm: false,
    directReports: [],
    profilesLoaded: true,
  });

  if (loading) return <CenteredLoader data-testid='centered-loader' />;
  if (!hasHadSummary) return <></>;

  return (
    <>
      <Row width='100%' widths={['50%', '50%']}>
        <Box>
          <Heading level={3} type='h300'>
            <FormattedMessage
              id='cs_perfportal_weekly_highlights_sentence_case'
              defaultMessage='Weekly Highlights'
            />
          </Heading>
        </Box>
      </Row>
      <ManagerWeeklyHighlightsDisclaimer />

      <CSASummariesContent
        spacingInset='400 none 400 none'
        {...CSASummariesContentProps}
      />
      <Row width='100%' spacingInset='400 none'>
        <Divider />
      </Row>
    </>
  );
};

export default AssociateCardExpanded;
